import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { BodyText, OtherText } from 'styling/Typography'
import {
  KpiRow,
  OverviewGrid,
  OverviewCard,
  OverviewCardRow,
  OverviewCardLeft,
  OverviewCardText,
  OverviewCardIcon,
  OverviewCardValue,
} from '../HrAnalytics.styled'
import { clearHrAnalytics, getOverview } from 'features/hrAnalytics/hrAnalytics.slice'
import { setTimeTo } from 'utils/helpers'
import { tokens } from 'styling/tokens'

const KpiIcons: Record<string, React.ReactNode> = {
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  person: <svg viewBox='0 0 24 24'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' /></svg>,
  dollar: <svg viewBox='0 0 24 24'><path d='M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z' /></svg>,
  food: <svg viewBox='0 0 24 24'><path d='M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 000 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z' /></svg>,
  calendar: <svg viewBox='0 0 24 24'><path d='M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z' /></svg>,
  truck: <svg viewBox='0 0 24 24'><path d='M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z' /></svg>,
}

const OVERVIEW_CARDS = [
  {
    key: 'attendance',
    title: 'Attendance Analytics',
    description: 'Presence outcomes, absence patterns, department comparisons.',
    iconKey: 'person',
    path: '/hr/analytics/attendance',
  },
  {
    key: 'schedule',
    title: 'Schedule Analytics',
    description: 'Planned obligations vs finalized outcomes.',
    iconKey: 'calendar',
    path: '/hr/analytics/schedule',
  },
  {
    key: 'payroll',
    title: 'Payroll Analytics',
    description: 'Payroll costs, overtime trends, department distribution.',
    iconKey: 'dollar',
    path: '/hr/analytics/payroll',
  },
  {
    key: 'logistics',
    title: 'Workforce Logistics',
    description: 'Food, accommodation, and mobility utilization.',
    iconKey: 'truck',
    path: '/hr/analytics/logistics',
  },
]

const toPercent = (value: number | null) => {
  if (value == null) return '—'
  const pct = value > 1 ? value : value * 100
  return `${pct.toFixed(1)}%`
}

const formatChange = (value: number | null) => {
  if (value == null) return undefined
  const pct = Math.abs(value) > 1 ? value : value * 100
  const sign = pct > 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)}%`
}

const formatCompactCurrency = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`
  return `$${value.toLocaleString()}`
}


export const OverviewTab = ({ dateFrom, dateTo }: IOverviewTab) => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { overview } = useSelector((state: RootState) => state.hrAnalytics)
  const startTs = dateFrom?.getTime()
  const endTs = dateTo?.getTime()

  useEffect(() => {
    if (!startTs || !endTs) return
    const params = {
      startDate: setTimeTo(new Date(startTs), 'start'),
      endDate: setTimeTo(new Date(endTs), 'end'),
    }
    dispatch(getOverview({ ...params }))
  }, [startTs, endTs, dispatch])

  useEffect(() => () => {
    dispatch(clearHrAnalytics())
  }, [])

  const kpis = useMemo(
    () => [
      {
        value: toPercent(overview?.attendanceRate ?? null),
        label: 'ATTENDANCE RATE',
        trend: overview?.attendanceRateChange != null
          ? {
            value: formatChange(overview.attendanceRateChange) || '',
            positive: (overview.attendanceRateChange ?? 0) >= 0,
          }
          : undefined,
        accentColor: tokens.status.success.fg,
        iconKey: 'checkCircle',
      },
      {
        value: toPercent(overview?.absenceRate ?? null),
        label: 'ABSENCE RATE',
        trend: overview?.absenceRateChange != null
          ? {
            value: formatChange(overview.absenceRateChange) || '',
            positive: (overview.absenceRateChange ?? 0) <= 0,
          }
          : undefined,
        accentColor: tokens.status.danger.fg,
        iconKey: 'person',
      },
      {
        value: formatCompactCurrency(overview?.totalPayroll ?? 0),
        label: 'TOTAL PAYROLL',
        accentColor: tokens.accent.base,
        iconKey: 'dollar',
      },
      {
        value: (overview?.mealsServed ?? 0).toLocaleString(),
        label: 'MEALS SERVED',
        sublabel: 'Across all kitchens',
        accentColor: tokens.status.info.fg,
        iconKey: 'food',
      },
    ],
    [overview],
  )

  const cardValues = useMemo(
    () => ({
      attendance: toPercent(overview?.attendanceRate ?? null),
      payroll: formatCompactCurrency(overview?.totalPayroll ?? 0),
      logistics: (overview?.mealsServed ?? 0).toLocaleString(),
    }),
    [overview],
  )

  return (
    <>
      <KpiRow>
        {kpis.map((kpi, idx) => (
          <MetricCard
            key={idx}
            layout='inline'
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            trend={kpi.trend}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <OverviewGrid>
        {OVERVIEW_CARDS.map((card) => {
          const value = cardValues[card.key as keyof typeof cardValues]
          return (
            <OverviewCard key={card.key} onClick={() => navigate(card.path)}>
              <OverviewCardRow>
                <OverviewCardLeft>
                  <OverviewCardIcon color={card.key === 'payroll' ? tokens.accent.base : undefined}>
                    {KpiIcons[card.iconKey]}
                  </OverviewCardIcon>
                  <OverviewCardText>
                    <BodyText weight='700'>{card.title}</BodyText>
                    <OtherText grey>{card.description}</OtherText>
                  </OverviewCardText>
                </OverviewCardLeft>
                {value && (
                  <OverviewCardValue color={card.key === 'logistics' ? tokens.accent.base : undefined}>
                    {value}
                  </OverviewCardValue>
                )}
              </OverviewCardRow>
            </OverviewCard>
          )
        })}
      </OverviewGrid>
    </>
  )
}

interface IOverviewTab {
  dateFrom: Date
  dateTo: Date
}
